import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, AlertCircle, Info, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

type CalloutTone = 'neutral' | 'attention' | 'success' | 'danger'

const TONE: Record<CalloutTone, { box: string; icon: string; Icon: LucideIcon }> = {
  // A note on the page: white, on a hairline, with the accent icon.
  neutral: { box: 'border-line bg-surface', icon: 'text-accent-ink', Icon: Info },
  // Something needs a look: an unconfirmed plan, a missing time.
  attention: { box: 'border-transparent bg-hold-soft', icon: 'text-hold-ink', Icon: AlertTriangle },
  success: { box: 'border-transparent bg-ok-soft', icon: 'text-ok-ink', Icon: CheckCircle2 },
  danger: { box: 'border-transparent bg-danger-soft', icon: 'text-danger-ink', Icon: AlertCircle },
}

interface CalloutProps {
  /** `neutral` for a note, `attention` for something to look at, `success`, `danger`. */
  tone?: CalloutTone
  /** Overrides the tone's own icon. */
  icon?: LucideIcon
  /** The fact, in a line. */
  title?: ReactNode
  /** What it means, or the detail. */
  children?: ReactNode
  /** One action that deals with it, on the right. */
  action?: ReactNode
  className?: string
}

/**
 * A notice inside a page, on Plan View's callout recipe: say the fact, then what
 * it means — "1 of 2 floor plans is not confirmed. It still tracks the venue, so
 * a remodel can move the furniture." — with at most one action beside it.
 *
 * For a note that belongs to the page, not for something that went wrong briefly
 * (that is a toast) or a strip across the whole app (a banner).
 */
export function Callout({ tone = 'neutral', icon, title, children, action, className }: CalloutProps) {
  const { box, icon: iconClass, Icon: ToneIcon } = TONE[tone]
  const Icon = icon ?? ToneIcon
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn('flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm text-ink', box, className)}
    >
      <Icon aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} />
      <div className="grid min-w-0 flex-1 gap-0.5">
        {title ? <p className="m-0 font-semibold">{title}</p> : null}
        {children ? (
          <div className={tone === 'neutral' ? 'text-ink-muted' : 'text-ink'}>{children}</div>
        ) : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  )
}
